import React, { useEffect, useMemo, useRef, useState } from "react";
import Section from "../components/Section";
import "../assets/styles/Produtos.css";

// => garanta que os arquivos existem exatamente com esses nomes:
import SajeImg from "../assets/images/saje.png";
import GoodweImg from "../assets/images/goodwe.png";
import IntelbrasImg from "../assets/images/intelbras.png";
import SungrowImg from "../assets/images/sungrow.png";
import SolisImg from "../assets/images/solis.png";
import TypingText from "../components/TypingText";

export type Brand = "Saje" | "Goodwe" | "Intelbras" | "Sungrow" | "Solis";

type InverterSpecs = {
  potenciaSaida?: string;
  mppt?: string;
  eficiencia?: string;
  tensaoEntrada?: string;
  protecoes?: string;
  comunicacao?: string;
  grauProtecao?: string;
  garantia?: string;
};

export type Inverter = {
  id: string;
  brand: Brand;
  model?: string;
  image: string;
  description?: string;
  specs?: InverterSpecs;
};

// --- Dataset (exemplo) ---
const INVERTERS: Inverter[] = [
  {
    id: "inv-saje-1",
    brand: "Saje",
    model: "SJA-5K",
    image: SajeImg,
    description: "Linha residencial eficiente com monitoramento por app.",
    specs: {
      potenciaSaida: "5 kW monofásico",
      mppt: "2 MPPT",
      eficiencia: "98.0%",
      tensaoEntrada: "120–600 V",
      protecoes: "AFCI, SPD, sobrecorrente",
      comunicacao: "Wi-Fi / App",
      grauProtecao: "IP65",
      garantia: "5 anos"
    }
  },
  {
    id: "inv-goodwe-1",
    brand: "Goodwe",
    model: "GW10K-ET",
    image: GoodweImg,
    description: "Série trifásica com ótima relação custo/benefício.",
    specs: {
      potenciaSaida: "10 kW trifásico",
      mppt: "2 MPPT",
      eficiencia: "98.3%",
      tensaoEntrada: "180–1000 V",
      protecoes: "AFCI, SPD, anti-ilhamento",
      comunicacao: "Wi-Fi / RS-485 / SEMS",
      grauProtecao: "IP65",
      garantia: "10 anos (países selecionados)"
    }
  },
  {
    id: "inv-intelbras-1",
    brand: "Intelbras",
    model: "IHV-6K",
    image: IntelbrasImg,
    description: "Integração nacional e assistência ampla.",
    specs: {
      potenciaSaida: "6 kW monofásico",
      mppt: "2 MPPT",
      eficiencia: "98.1%",
      tensaoEntrada: "120–600 V",
      protecoes: "SPD CC/CA, sobretemp.",
      comunicacao: "Wi-Fi / App",
      grauProtecao: "IP65",
      garantia: "7 anos (Brasil)"
    }
  },
  {
    id: "inv-sungrow-1",
    brand: "Sungrow",
    model: "SG10RT",
    image: SungrowImg,
    description: "Alta eficiência e robustez reconhecida no mercado.",
    specs: {
      potenciaSaida: "10 kW trifásico",
      mppt: "3 MPPT",
      eficiencia: "98.6%",
      tensaoEntrada: "150–1000 V",
      protecoes: "AFCI, SPD Tipo II",
      comunicacao: "Wi-Fi / Ethernet / iSolarCloud",
      grauProtecao: "IP65",
      garantia: "10 anos"
    }
  },
  {
    id: "inv-solis-1",
    brand: "Solis",
    model: "S5-GR1P(6)K",
    image: SolisImg,
    description: "Boa performance com interface simples.",
    specs: {
      potenciaSaida: "6 kW monofásico",
      mppt: "2 MPPT",
      eficiencia: "98.4%",
      tensaoEntrada: "90–600 V",
      protecoes: "AFCI, SPD, rele anti-ilhamento",
      comunicacao: "Wi-Fi / RS-485",
      grauProtecao: "IP66",
      garantia: "10 anos"
    }
  }
];

// --- Modal de Produto ---
const ProdutoModal: React.FC<{
  item: Inverter | null;
  onClose: () => void;
}> = ({ item, onClose }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  // fecha com ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // foco inicial no botão fechar
  useEffect(() => {
    if (item && closeBtnRef.current) closeBtnRef.current.focus();
  }, [item]);

  if (!item) return null;

  const { specs } = item;

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Detalhes do inversor ${item.brand} ${item.model ?? ""}`}
      onClick={onClose}
      ref={overlayRef}
    >
      <div className="modal-card" onClick={stop}>
        <header className="modal-header">
          <h3>
            {item.brand} {item.model && <span>• {item.model}</span>}
          </h3>
          <button
            ref={closeBtnRef}
            className="modal-close"
            onClick={onClose}
            aria-label="Fechar detalhes"
          >
            ✕
          </button>
        </header>

        <div className="modal-body">
          <div className="modal-figure">
            <img
              src={item.image}
              alt={`${item.brand} ${item.model ?? ""}`}
              loading="lazy"
            />
          </div>

          <div className="modal-content">
            {item.description && <p className="modal-desc">{item.description}</p>}

            {specs && (
              <ul className="modal-specs">
                {specs.potenciaSaida && (
                  <li><strong>Potência de saída:</strong> {specs.potenciaSaida}</li>
                )}
                {specs.mppt && (
                  <li><strong>MPPT:</strong> {specs.mppt}</li>
                )}
                {specs.eficiencia && (
                  <li><strong>Eficiência máx.:</strong> {specs.eficiencia}</li>
                )}
                {specs.tensaoEntrada && (
                  <li><strong>Tensão de entrada:</strong> {specs.tensaoEntrada}</li>
                )}
                {specs.protecoes && (
                  <li><strong>Proteções:</strong> {specs.protecoes}</li>
                )}
                {specs.comunicacao && (
                  <li><strong>Comunicação:</strong> {specs.comunicacao}</li>
                )}
                {specs.grauProtecao && (
                  <li><strong>Grau de proteção:</strong> {specs.grauProtecao}</li>
                )}
                {specs.garantia && (
                  <li><strong>Garantia:</strong> {specs.garantia}</li>
                )}
              </ul>
            )}
          </div>
        </div>

        <footer className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Fechar</button>
        </footer>
      </div>
    </div>
  );
};

// --- Card simples (agora clicável) ---
const ProdutoCard: React.FC<{
  item: Inverter;
  onClick?: (item: Inverter) => void;
}> = ({ item, onClick }) => (
  <article
    className="product-card"
    role="button"
    tabIndex={0}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.(item)}
    onClick={() => onClick?.(item)}
    aria-label={`Abrir detalhes de ${item.brand} ${item.model ?? ""}`}
  >
    <div className="product-image">
      <img src={item.image} alt={`${item.brand} ${item.model ?? ""}`} loading="lazy" />
    </div>
    <h3 className="product-title">
      {item.brand} {item.model && `• ${item.model}`}
    </h3>
    {item.description && <p className="product-desc">{item.description}</p>}
    <p className="product-keywords">Inversor solar {item.brand} para energia fotovoltaica · ficha técnica e aplicação</p>
    <span className="product-open">Ver ficha técnica <span aria-hidden="true">↗</span></span>
  </article>
);

// --- Página de Produtos ---
const Produtos: React.FC = () => {
  const [selected, setSelected] = useState<Inverter | null>(null);
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState<Brand | "Todos">("Todos");
  const filteredInverters = useMemo(() => INVERTERS.filter((item) => {
    const text = `${item.brand} ${item.model} ${item.description}`.toLowerCase();
    return (brand === "Todos" || item.brand === brand) && text.includes(query.toLowerCase());
  }), [brand, query]);

  return (
    <div className="produtos-page no-offset">
      {/* <Section className="services-section">
        <div className="container text-center">
          <h1 className="section-title">Produtos</h1>
        </div>
      </Section> */}

        <Section className="services-section pre-sale-hero">
          <div className="container text-center">
            <div className="container text-center">
              <h1 className="section-title">Exclusivo</h1>
              <TypingText text="Garanta condições especiais em inversores Saje, GoodWe, Intelbras, Sungrow e Solis." />
            </div>

            <div className="benefit-cards">
              <div className="benefit-card">
                <div className="benefit-icon">
                  {/* Consultoria */}
                  <svg viewBox="0 0 26 24" width="25" height="25" aria-hidden="true">
                    <path d="M12 2a7 7 0 0 0-7 7c0 3.87 3.134 7 7 7h1v3l4-3h1a7 7 0 0 0 0-14h-6z" fill="currentColor"/>
                  </svg>
                </div>
                <h5>Consultoria personalizada</h5>
                <p>Projeto sob medida para sua necessidade.</p>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  {/* Garantia */}
                  <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
                    <path d="M12 2l7 3v6c0 5-3.5 9.74-7 11-3.5-1.26-7-6-7-11V5l7-3z" fill="currentColor"/>
                  </svg>
                </div>
                <h5>Produtos com garantia</h5>
                <p>Equipamentos originais e certificados.</p>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  {/* Instalação */}
                  <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
                    <path d="M3 13h18v2H3v-2zm3-6h12l2 4H4l2-4zm2 10h8v5H8v-5z" fill="currentColor"/>
                  </svg>
                </div>
                <h5>Instalação & monitoramento</h5>
                <p>Time técnico, app e acompanhamento contínuo.</p>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  {/* Economia */}
                  <svg viewBox="0 0 24 24" width="25" height="25" aria-hidden="true">
                    <path d="M12 1a11 11 0 1 0 11 11A11.013 11.013 0 0 0 12 1zm1 17h-2v-1.05A3.5 3.5 0 0 1 8.5 13H8v-2h.5A3.5 3.5 0 0 1 11 7.05V6h2v1.05A3.5 3.5 0 0 1 15.5 11H16v2h-.5A3.5 3.5 0 0 1 13 16.95V18z" fill="currentColor"/>
                  </svg>
                </div>
                <h5>Economia real</h5>
                <p>Redução da conta já nos primeiros meses.</p>
              </div>
            </div>

  
          </div>
        </Section>

        <Section className="services-section product-catalog-section">
          <div className="container text-center">
            <div className="product-catalog-heading">
              <span className="product-kicker">INVERSORES SOLARES</span>
              <h1 className="section-title">Escolha o equipamento ideal para o seu projeto</h1>
              <p>Compare inversores fotovoltaicos Saje, GoodWe, Intelbras, Sungrow e Solis. Clique em um produto para ver potência, eficiência, proteção e garantia.</p>
            </div>
            <div className="product-filters" role="search">
              <label className="sr-only" htmlFor="product-search">Buscar inversor solar</label>
              <input id="product-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por marca, modelo ou aplicação" />
              <div className="brand-filters" aria-label="Filtrar por marca">
                {(["Todos", "Saje", "Goodwe", "Intelbras", "Sungrow", "Solis"] as const).map((item) => <button key={item} className={brand === item ? "active" : ""} onClick={() => setBrand(item)}>{item}</button>)}
              </div>
            </div>
            <Section>
              <div className="container">
                <div className="products-grid">
                  {filteredInverters.map((inv) => (
                    <ProdutoCard key={inv.id} item={inv} onClick={setSelected} />
                  ))}
                </div>
                {filteredInverters.length === 0 && <p className="empty-products">Nenhum inversor encontrado. Tente outra marca ou modelo.</p>}
              </div>
            </Section>
          </div>
        </Section>

      {/* Modal */}
      <ProdutoModal item={selected} onClose={() => setSelected(null)} />

        <section className="cta-section">
          <div className="container">
            <span className="cta-kicker">SUA CONTA NÃO PRECISA CONTINUAR ALTA</span>
            <h2 className="section-title center">Descubra quanto você pode economizar</h2>
            <p>Escolha o inversor certo para o seu consumo e dê o primeiro passo para reduzir sua conta de energia.</p>
            <a
              href="https://wa.me/556232949182?text=Olá! Tenho interesse na pré-venda dos inversores."
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-button"
              aria-label="Descobrir quanto posso economizar pelo WhatsApp"
            >
              <i className="fab fa-whatsapp" /> Quero calcular minha economia
            </a>
            
            <a
            className="whats-fab"
            href="https://wa.me/556232949182"
            target="_blank"
            rel="noreferrer"
            aria-label="WhatsApp"
            >
              <div className="whats-chat">
                <div className="whats-header">WhatsApp</div>
                <div className="whats-body">
                  <p>👋 Olá! Entrar em contato com um de nossos especialistas</p>
                </div>
              </div>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 32 32"
                width="28"
                height="28"
                fill="currentColor"
              >
                <path d="M16 0C7.163 0 0 6.99 0 15.615c0 2.755.765 5.393 2.212 7.705L0 32l8.99-2.35c2.44 1.333 5.2 2.04 8.01 2.04 8.837 0 16-6.99 16-15.615C33 6.99 24.837 0 16 0zm0 29.333c-2.49 0-4.92-.653-7.054-1.89l-.505-.29-5.328 1.393 1.426-5.086-.33-.522c-1.324-2.082-2.023-4.49-2.023-6.992 0-7.164 6.09-13 13.5-13 7.41 0 13.5 5.836 13.5 13 0 7.165-6.09 13-13.5 13zm7.374-9.75c-.405-.2-2.386-1.176-2.755-1.309-.37-.133-.64-.2-.91.2-.27.398-1.045 1.308-1.28 1.576-.235.267-.47.3-.874.1-.405-.2-1.71-.629-3.26-2.002-1.205-1.067-2.015-2.388-2.25-2.787-.235-.398-.025-.614.176-.813.18-.177.405-.46.61-.69.2-.23.27-.397.405-.664.135-.266.07-.498-.035-.698-.105-.2-.91-2.177-1.245-2.985-.33-.798-.675-.69-.91-.704l-.775-.014c-.27 0-.71.1-1.08.498-.37.398-1.41 1.376-1.41 3.35s1.445 3.887 1.646 4.155c.2.266 2.84 4.48 6.89 6.086.962.416 1.71.664 2.295.851.962.306 1.84.262 2.535.159.775-.116 2.386-.974 2.725-1.913.335-.94.335-1.745.235-1.913-.095-.164-.37-.266-.775-.466z"/>
              </svg>
            </a>

          </div>
          </section>
    </div>
  );
};

export default Produtos;
